import React, { useState, useEffect, useRef, lazy, Suspense, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Paper,
    Autocomplete,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircleOutline as CheckIcon,
    Send as SendIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    Add as AddIcon
} from '@mui/icons-material';
import SelectEmployeesAndTempsModal from '../jha/SelectEmployeesAndTempsModal';
import FormNavigator from './FormNavigator';
import incidentService, { INCIDENT_STAGE } from '../../services/incidentService';
import witnessService from '../../services/witnessService';
import { initializeDB } from '../../config/db';
import { JOB_STORE } from '../../config/constants';
import useDictation from '../../hooks/useDictation';

// Lazy load forms
const IncidentFormMain = lazy(() => import('./IncidentFormMain'));
const DrugTestSection = lazy(() => import('./DrugTestSection'));
const MedicalRefusalSection = lazy(() => import('./MedicalRefusalSection'));
const WitnessForm = lazy(() => import('./WitnessForm'));
const AutoIncidentForm = lazy(() => import('./AutoIncidentForm'));

const INCIDENT_TYPES = [
    'Near Miss/Close Call',
    'First Aid (on-site care provided)',
    'Minor Injury (injury using off-site medical treatment)',
    'Major Injury (emergency services required)',
    'Property Damage',
    'Auto Incident'
];

const IncidentFormStep1New = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [incident, setIncident] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [witnessModalOpen, setWitnessModalOpen] = useState(false);
    const [locationType, setLocationType] = useState('job');
    const [errors, setErrors] = useState([]);
    const [step1Submitted, setStep1Submitted] = useState(false);
    const [initialInfoExpanded, setInitialInfoExpanded] = useState(true);
    const [activeFormId, setActiveFormId] = useState('main');
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [expandedColumns, setExpandedColumns] = useState({});
    // Дозаполнять пакет имеет право только тот, кого назначили во внешней базе.
    // Репортёр после Initial Report видит запись, но ничего в ней не заполняет.
    const [canFillFollowUps, setCanFillFollowUps] = useState(false);
    const saveTimeoutRef = useRef();
    const lastSavedDataRef = useRef(null);
    const isSavingRef = useRef(false);
    
    // Form data storage
    const [drugTestData, setDrugTestData] = useState({});
    const [medicalRefusalData, setMedicalRefusalData] = useState({});
    const [completionStatus, setCompletionStatus] = useState({});
    
    // Speech recognition (unified)
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });
    const currentDescriptionRef = useRef('');

    const { control, handleSubmit, watch, setValue, formState: { errors: formErrors } } = useForm({
        defaultValues: {
            incidentDateTime: '',
            employees: [],
            witnesses: [],
            location: {
                type: 'job',
                location: ''
            },
            description: '',
            incidentType: 'Near Miss/Close Call',
            incidentTypes: []
        }
    });

    const watchedValues = watch();

    // Calculate viewport height for proper sizing
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const availableHeight = viewportHeight - 64; // Subtract header height (64px on tablets/desktop)

    // Update description ref when it changes
    useEffect(() => {
        currentDescriptionRef.current = watchedValues.description || '';
    }, [watchedValues.description]);

    // No local SR lifecycle — handled by useDictation

    const speechSupported = dictationSupported; // preserve existing naming

    const toggleSpeechRecognition = async () => {
        if (!speechSupported) {
            console.error('Speech recognition not supported');
            return;
        }
        if (isListening) {
            stopDictation();
        } else {
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    const currentText = currentDescriptionRef.current || '';
                    const newText = currentText + (currentText ? ' ' : '') + transcript;
                    setValue('description', newText);
                    currentDescriptionRef.current = newText;
                },
                onError: (e) => {
                    console.error('Recognition error:', e?.error || e);
                },
            });
        }
    };

    // Helpers for incident types (support legacy single value)
    const selectedTypes = useMemo(() => {
        const types = Array.isArray(watchedValues.incidentTypes) && watchedValues.incidentTypes.length > 0
            ? watchedValues.incidentTypes
            : (watchedValues.incidentType ? [watchedValues.incidentType] : []);
        return new Set(types);
    }, [watchedValues.incidentTypes, watchedValues.incidentType]);

    const isNearMissOnly = useMemo(() => selectedTypes.size === 1 && selectedTypes.has('Near Miss/Close Call'), [selectedTypes]);
    const hasAutoIncident = useMemo(() => selectedTypes.has('Auto Incident'), [selectedTypes]);
    const isAutoOnly = useMemo(() => selectedTypes.size === 1 && selectedTypes.has('Auto Incident'), [selectedTypes]);

    // Generate forms list based on incident data
    const getForms = () => {
        const forms = [];

        // Include forms depending on selected types
        if (isAutoOnly) {
            // Only auto incident form
            forms.push({ id: 'auto', type: 'auto', label: 'Auto Incident Report', required: true });
        } else {
            // Always include main report if not auto-only (includes Near Miss)
            forms.push({ id: 'main', type: 'main', label: 'Full Report', required: true });

            // If Auto is also selected along with others, include auto form too
            if (hasAutoIncident) {
                forms.push({ id: 'auto', type: 'auto', label: 'Auto Incident Report', subtitle: 'Auto Incident', required: true });
            }
        }

        // Add medical/drug test forms for employees (required regardless of auto-only)
        if (watchedValues.employees?.length > 0) {
            
            // Add medical refusal forms
            watchedValues.employees.forEach((emp, index) => {
                forms.push({
                    id: `medical-${index}`,
                    type: 'medical',
                    label: `Medical Refusal`,
                    subtitle: emp.FullName || emp.fullName,
                    participantIndex: index,
                    required: false
                });
            });

            // Add drug test forms
            watchedValues.employees.forEach((emp, index) => {
                forms.push({
                    id: `drugtest-${index}`,
                    type: 'drugtest',
                    label: `Drug Test`,
                    subtitle: emp.FullName || emp.fullName,
                    participantIndex: index,
                    required: true
                });
            });
        }

        // Add witness forms if witnesses exist
        if (watchedValues.witnesses?.length > 0) {
            watchedValues.witnesses.forEach((witness, index) => {
                forms.push({
                    id: `witness-${index}`,
                    type: 'witness',
                    label: `Witness Statement`,
                    subtitle: witness.FullName || witness.fullName,
                    witnessIndex: index,
                    required: false
                });
            });
        }

        return forms;
    };

    // Load jobs from IndexedDB
    useEffect(() => {
        loadJobs();
    }, []);

    // Load or create incident
    useEffect(() => {
        loadIncident();
    }, [id]);

    // Auto-save on form changes
    useEffect(() => {
        if (!incident?.id || loading || isSavingRef.current) return;
        // Отправленный инцидент, который ведёт не этот человек, автосейвом
        // трогать нельзя: перезапишем payload пустыми под-формами.
        if (step1Submitted && !canFillFollowUps) return;

        const currentDataString = JSON.stringify({ 
            ...watchedValues, 
            drugTests: drugTestData,
            medicalRefusals: medicalRefusalData 
        });
        
        if (lastSavedDataRef.current === currentDataString) return;

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            saveFormData();
            lastSavedDataRef.current = currentDataString;
        }, 500);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [watchedValues, drugTestData, medicalRefusalData, step1Submitted, canFillFollowUps]);

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const jobStore = tx.objectStore(JOB_STORE);
            const allJobs = await jobStore.getAll();

            const activeJobs = allJobs.filter(job => job && (job.Job || job.JobName));
            activeJobs.sort((a, b) => {
                const nameA = (a.Job || '').toLowerCase();
                const nameB = (b.Job || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });

            setJobs(activeJobs);
        } catch (error) {
            console.error('Error loading jobs:', error);
        }
    };

    const loadIncident = async () => {
        setLoading(true);
        try {
            if (id === 'new') {
                const newDraft = await incidentService.createDraft();
                navigate(`/incidents/${newDraft.uuid}/edit`, { replace: true });
                return;
            }

            const existingIncident = await incidentService.getByUUID(id);
            if (existingIncident) {
                setIncident(existingIncident);

                // Set form values
                if (existingIncident.payload) {
                    const payload = existingIncident.payload;
                    setValue('incidentDateTime', payload.incidentDateTime || '');
                    setValue('employees', payload.employees || []);
                    setValue('witnesses', payload.witnesses || []);
                    setValue('location', payload.location || { type: 'job', location: '' });
                    setValue('description', payload.description || '');
                    setValue('incidentType', payload.incidentType || 'Near Miss/Close Call');
                    if (Array.isArray(payload.incidentTypes)) {
                        setValue('incidentTypes', payload.incidentTypes);
                    }
                    setLocationType(payload.location?.type || 'job');

                    // Load drug test and medical refusal data
                    if (payload.drugTests) setDrugTestData(payload.drugTests);
                    if (payload.medicalRefusals) setMedicalRefusalData(payload.medicalRefusals);
                }

                // Check status
                if (existingIncident.status === 'step1_submitted' || 
                    existingIncident.status === 'submitted' ||
                    existingIncident.status === 'fullReportSubmitted') {
                    setStep1Submitted(true);
                    setInitialInfoExpanded(false);
                }

                if (existingIncident.status === 'submitted' ||
                    existingIncident.status === 'fullReportSubmitted') {
                    setIsReadOnly(true);
                }

                // Пакет открыт только назначенному и только пока инцидент не закрыт.
                setCanFillFollowUps(
                    incidentService.isAssignedToMe(existingIncident)
                    && existingIncident.workflowStage !== INCIDENT_STAGE.CLOSED
                );

                const completionUpdates = {};
                const payloadForStatuses = existingIncident.payload || {};
                const incidentTypes = Array.isArray(payloadForStatuses.incidentTypes) ? payloadForStatuses.incidentTypes : (payloadForStatuses.incidentType ? [payloadForStatuses.incidentType] : []);
                const includesAutoType = incidentTypes.includes('Auto Incident');

                if (existingIncident.mainReportSubmitted === true ||
                    existingIncident.status === 'submitted' ||
                    existingIncident.status === 'fullReportSubmitted' ||
                    existingIncident.status === 'completed') {
                    completionUpdates.main = true;
                }

                if (includesAutoType || (incidentTypes.length === 0 && payloadForStatuses.incidentType === 'Auto Incident')) {
                    if (!existingIncident.needsFullReportSync &&
                        (existingIncident.status === 'fullReportSubmitted' || existingIncident.status === 'completed')) {
                        completionUpdates.auto = true;
                    }
                }

                // Check witness submission statuses
                if (existingIncident.payload?.witnesses && existingIncident.payload.witnesses.length > 0) {
                    const witnessStatuses = {};
                    for (let i = 0; i < existingIncident.payload.witnesses.length; i++) {
                        try {
                            const witnessData = await witnessService.getWitnessForm(existingIncident.uuid, `witness-${i}`);
                            if (witnessData && witnessData.status === 'submitted') {
                                witnessStatuses[`witness-${i}`] = true;
                            }
                        } catch (err) {
                            console.error(`Error checking witness ${i} status:`, err);
                        }
                    }
                    setCompletionStatus(prev => ({ ...prev, ...witnessStatuses }));
                }
                
                // Check drug test statuses
                if (existingIncident.payload?.drugTests) {
                    const drugTestStatuses = {};
                    Object.keys(existingIncident.payload.drugTests).forEach(index => {
                        if (existingIncident.payload.drugTests[index]?.submitted) {
                            drugTestStatuses[`drugtest-${index}`] = true;
                        }
                    });
                    setCompletionStatus(prev => ({ ...prev, ...drugTestStatuses }));
                }
                
                // Check medical refusal statuses
                if (existingIncident.payload?.medicalRefusals) {
                    const medicalStatuses = {};
                    Object.keys(existingIncident.payload.medicalRefusals).forEach(index => {
                        if (existingIncident.payload.medicalRefusals[index]?.submitted) {
                            medicalStatuses[`medical-${index}`] = true;
                        }
                    });
                    setCompletionStatus(prev => ({ ...prev, ...medicalStatuses }));
                }

                // Что уже сдано — по данным сервера. Локальные проверки выше видят
                // только этот айпад, а инцидент мог начинать другой человек.
                const serverCompleted = existingIncident.serverCompleted;
                if (serverCompleted) {
                    if (serverCompleted.main) completionUpdates.main = true;
                    if (serverCompleted.auto) completionUpdates.auto = true;
                    (serverCompleted.witnesses || []).forEach(i => { completionUpdates[`witness-${i}`] = true; });
                    (serverCompleted.drugTests || []).forEach(i => { completionUpdates[`drugtest-${i}`] = true; });
                    (serverCompleted.medicalRefusals || []).forEach(i => { completionUpdates[`medical-${i}`] = true; });
                }

                if (Object.keys(completionUpdates).length > 0) {
                    setCompletionStatus(prev => ({ ...prev, ...completionUpdates }));
                }
            } else {
                const newDraft = await incidentService.createDraft();
                navigate(`/incidents/${newDraft.uuid}/edit`, { replace: true });
            }
        } catch (error) {
            console.error('Error loading incident:', error);
            setErrors(['Failed to load incident']);
        } finally {
            setLoading(false);
        }
    };

    const saveFormData = async () => {
        if (!incident?.id || isSavingRef.current) return;
        
        isSavingRef.current = true;
        setSaving(true);
        
        try {
            const formData = {
                ...watchedValues,
                drugTests: drugTestData,
                medicalRefusals: medicalRefusalData
            };
            
            await incidentService.updateDraft(incident.id, formData);
        } catch (error) {
            console.error('Error saving form data:', error);
        } finally {
            setSaving(false);
            isSavingRef.current = false;
        }
    };

    const handleLocationTypeChange = (newType) => {
        setLocationType(newType);
        setValue('location', { type: newType, location: '' });
    };

    const handleJobSelect = (event, newValue) => {
        if (newValue) {
            const jobCode = newValue.Job || '';
            const jobName = newValue.JobName || '';
            const locationValue = jobCode && jobName ? 
                `${jobCode} - ${jobName}` : (jobCode || jobName || `Job #${newValue.id}`);
            setValue('location', { 
                type: 'job', 
                location: locationValue,
                jobNumber: jobCode,
                id: newValue.id 
            });
        } else {
            setValue('location', { type: 'job', location: '' });
        }
    };

    const handleLocationSelect = (event, newValue) => {
        if (newValue) {
            const locationValue = newValue.location || newValue.Location || `Location #${newValue.id}`;
            setValue('location', { 
                type: 'location', 
                location: locationValue,
                id: newValue.id 
            });
        } else {
            setValue('location', { type: 'location', location: '' });
        }
    };

    const getSelectedJob = () => {
        const location = watchedValues.location;
        if (location?.type === 'job' && location?.id) {
            return jobs.find(j => j.id === location.id) || null;
        }
        return null;
    };

    const getSelectedLocation = () => {
        const location = watchedValues.location;
        if (location?.type === 'location' && location?.id) {
            return jobs.find(j => j.id === location.id) || null;
        }
        return null;
    };

    const handleStep1Submit = async () => {
        const validationErrors = [];
        
        if (!watchedValues.incidentDateTime) {
            validationErrors.push('Date and time is required');
        }
        if (!watchedValues.employees?.length) {
            validationErrors.push('At least one employee must be selected');
        }
        if (!watchedValues.location?.location) {
            validationErrors.push('Location is required');
        }
        if (!watchedValues.description || watchedValues.description.trim().length < 10) {
            validationErrors.push('Description must be at least 10 characters');
        }
        const types = Array.isArray(watchedValues.incidentTypes) ? watchedValues.incidentTypes : (watchedValues.incidentType ? [watchedValues.incidentType] : []);
        if (types.length === 0) {
            validationErrors.push('Please select at least one incident type');
        }
        
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            return;
        }
        
        await saveFormData();
        
        const typesArr = Array.isArray(watchedValues.incidentTypes) && watchedValues.incidentTypes.length > 0
            ? watchedValues.incidentTypes
            : (watchedValues.incidentType ? [watchedValues.incidentType] : []);
        const legacyType = typesArr.length > 0 ? typesArr[0] : '';
        const initialReportData = {
            incidentDateTime: watchedValues.incidentDateTime,
            incidentType: legacyType,
            incidentTypes: typesArr,
            location: watchedValues.location,
            description: watchedValues.description,
            employees: watchedValues.employees,
            witnesses: watchedValues.witnesses,
            drugTests: drugTestData || {},
            reportBy: {
                employeeId: localStorage.getItem('employeeId'),
                firstName: localStorage.getItem('firstName'),
                lastName: localStorage.getItem('lastName')
            },
            reportDate: new Date().toISOString()
        };
        
        if (incident?.id) {
            try {
                await incidentService.submitIncident(incident.id, initialReportData);
                
                const updatedIncident = await incidentService.getById(incident.id);
                setIncident(updatedIncident);
                setStep1Submitted(true);
                setInitialInfoExpanded(false);
                
            } catch (error) {
                console.error('Error submitting initial report:', error);
                setErrors(['Failed to submit initial report']);
            }
        }
    };

    const renderActiveForm = () => {
        const forms = getForms();
        let activeForm = forms.find(f => f.id === activeFormId);
        if (!activeForm && forms.length > 0) {
            activeForm = forms[0];
        }
        
        if (!activeForm || !incident) return null;

        // Main full report form
        if (activeForm.type === 'main') {
            return (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}>
                    <IncidentFormMain 
                        key={incident.id}
                        incidentId={incident.id}
                        incidentData={incident}
                        embedded={true}
                        onCompletionChange={(isComplete) => {
                            setCompletionStatus(prev => ({ ...prev, main: isComplete }));
                        }}
                    />
                </Suspense>
            );
        }

        // Auto incident form
        if (activeForm.type === 'auto') {
            return (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}>
                    <AutoIncidentForm
                        key={`${incident.id}-auto`}
                        incident={incident}
                        employees={watchedValues.employees || []}
                        embedded={true}
                        onCompletionChange={(isComplete) => {
                            setCompletionStatus(prev => ({ ...prev, auto: isComplete }));
                        }}
                    />
                </Suspense>
            );
        }

        // Medical refusal form
        if (activeForm.type === 'medical') {
            const participant = watchedValues.employees[activeForm.participantIndex];
            return (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}>
                    <MedicalRefusalSection
                        key={activeForm.id}
                        medicalRefusalData={medicalRefusalData[activeForm.participantIndex] || {}}
                        onChange={(newData) => {
                            setMedicalRefusalData(prev => ({
                                ...prev,
                                [activeForm.participantIndex]: newData
                            }));
                        }}
                        disabled={false}
                        incidentUuid={incident?.uuid}
                        participantIndex={activeForm.participantIndex}
                        participantData={participant}
                        participantType={(participant && (participant._type === 'temp' || participant.isTemp === true)) ? 'temp' : 'employee'}
                        onCompletionChange={(isComplete) => {
                            setCompletionStatus(prev => ({ 
                                ...prev, 
                                [activeForm.id]: isComplete
                            }));
                        }}
                    />
                </Suspense>
            );
        }

        // Drug test form
        if (activeForm.type === 'drugtest') {
            const participant = watchedValues.employees[activeForm.participantIndex];
            return (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}>
                    <DrugTestSection
                        key={activeForm.id}
                        drugTestData={drugTestData[activeForm.participantIndex] || {}}
                        onChange={(newData) => {
                            setDrugTestData(prev => ({
                                ...prev,
                                [activeForm.participantIndex]: newData
                            }));
                        }}
                        disabled={false}
                        incidentUuid={incident?.uuid}
                        participantIndex={activeForm.participantIndex}
                        participantData={participant}
                        participantType={(participant && (participant._type === 'temp' || participant.isTemp === true)) ? 'temp' : 'employee'}
                        onCompletionChange={(isComplete) => {
                            setCompletionStatus(prev => ({ 
                                ...prev, 
                                [activeForm.id]: isComplete
                            }));
                        }}
                    />
                </Suspense>
            );
        }

        // Witness form
        if (activeForm.type === 'witness') {
            const witness = watchedValues.witnesses[activeForm.witnessIndex];
            return (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}>
                    <WitnessForm
                        key={activeForm.id}
                        incidentId={id}
                        witnessId={`witness-${activeForm.witnessIndex}`}
                        witness={witness}
                        incident={incident}
                        onRemove={!step1Submitted ? () => {
                            const newWitnesses = watchedValues.witnesses.filter((_, i) => i !== activeForm.witnessIndex);
                            setValue('witnesses', newWitnesses);
                        } : null}
                        onSubmitSuccess={() => {
                            setCompletionStatus(prev => ({ 
                                ...prev, 
                                [activeForm.id]: true
                            }));
                        }}
                    />
                </Suspense>
            );
        }

        return null;
    };

    const forms = getForms();
    
    // Calculate dynamic navigator height based on expanded state
    const navigatorHeight = useMemo(() => {
        if (!step1Submitted || !canFillFollowUps) return 0;

        const groupedForms = {
            // auto forms are shown in the main column in the navigator
            main: forms.filter(f => f.type === 'main' || f.type === 'auto'),
            medical: forms.filter(f => f.type === 'medical'),
            drugtest: forms.filter(f => f.type === 'drugtest'),
            witness: forms.filter(f => f.type === 'witness')
        };
        
        let maxVisibleItems = 0;
        Object.keys(groupedForms).forEach(type => {
            const count = groupedForms[type].length;
            if (expandedColumns[type] && count > 3) {
                // When expanded, show ALL items height
                maxVisibleItems = Math.max(maxVisibleItems, count);
            } else {
                // Show actual count up to 3
                const visibleCount = Math.min(3, count);
                maxVisibleItems = Math.max(maxVisibleItems, visibleCount);
            }
        });
        
        // Cap at 7 items max to prevent too tall navigator
        maxVisibleItems = Math.min(maxVisibleItems, 7);
        
        // Header (24px) + items * 29px (button height + margin) + padding (10px)
        return 24 + maxVisibleItems * 29 + 10;
    }, [step1Submitted, canFillFollowUps, expandedColumns, forms.length]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{
                position: 'fixed',
                top: '64px',
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.default',
                height: `calc(100vh - 64px)`
            }}>
                {/* Compact Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    minHeight: 48
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton onClick={() => navigate('/incidents')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                            {canFillFollowUps
                                ? 'Incident Report'
                                : 'Initial Incident Communication'}
                        </Typography>
                        {incident?.siNumber && (
                            <Chip label={incident.siNumber} size="small" variant="outlined" sx={{ height: 20 }} />
                        )}
                    </Box>
                    {saving && (
                        <Chip label="Saving..." size="small" color="info" sx={{ height: 20 }} />
                    )}
                </Box>

                {/* Initial Info Form */}
                {!step1Submitted ? (
                    <Box sx={{ flex: '0 0 auto', p: 2 }}>
                        <Paper sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
                            {errors.length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors([])}>
                                    {errors.map((error, index) => (
                                        <div key={index}>{error}</div>
                                    ))}
                                </Alert>
                            )}
                            <Stack spacing={2}>
                            {/* First Row: Date/Time and Location */}
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Controller
                                        name="incidentDateTime"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <DateTimePicker
                                                label="Date/Time"
                                                value={field.value ? dayjs(field.value) : null}
                                                onChange={(newValue) => {
                                                    field.onChange(newValue ? newValue.toISOString() : '');
                                                }}
                                                maxDateTime={dayjs()}
                                                disabled={step1Submitted || isReadOnly}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: "small",
                                                        error: !!formErrors.incidentDateTime,
                                                        sx: { '& .MuiInputBase-root': { height: 40 } }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <Box sx={{ position: 'relative' }}>
                                        <RadioGroup
                                            row
                                            value={locationType}
                                            onChange={(e) => !step1Submitted && handleLocationTypeChange(e.target.value)}
                                            sx={{ position: 'absolute', top: -38, zIndex: 1 }}
                                        >
                                            <FormControlLabel
                                                value="job"
                                                control={<Radio size="small" disabled={step1Submitted} />}
                                                label="Job"
                                                sx={{ mr: 2, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                            />
                                            <FormControlLabel
                                                value="location"
                                                control={<Radio size="small" disabled={step1Submitted} />}
                                                label="Location"
                                                sx={{ mr: 2, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                            />
                                            <FormControlLabel
                                                value="manual"
                                                control={<Radio size="small" disabled={step1Submitted} />}
                                                label="Manual"
                                                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                            />
                                        </RadioGroup>

                                        {locationType === 'job' && (
                                            <Autocomplete
                                                size="small"
                                                options={jobs}
                                                value={getSelectedJob()}
                                                onChange={handleJobSelect}
                                                disabled={step1Submitted || isReadOnly}
                                                getOptionLabel={(option) => {
                                                    const jobCode = option.Job || '';
                                                    const jobName = option.JobName || '';
                                                    return jobCode && jobName ? `${jobCode} - ${jobName}` : (jobCode || jobName);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Job"
                                                        sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                                    />
                                                )}
                                                fullWidth
                                            />
                                        )}

                                        {locationType === 'location' && (
                                            <Autocomplete
                                                size="small"
                                                options={jobs}
                                                value={getSelectedLocation()}
                                                onChange={handleLocationSelect}
                                                disabled={step1Submitted || isReadOnly}
                                                getOptionLabel={(option) => option.location || option.Location || `Location #${option.id}`}
                                                getOptionKey={(option) => option.id}
                                                filterOptions={(options, { inputValue }) => {
                                                    const normalizedInput = inputValue.toLowerCase();
                                                    // Filter and deduplicate by location name
                                                    const seen = new Set();
                                                    return options.filter((option) => {
                                                        const locationName = (option.location || option.Location || '').toLowerCase();
                                                        if (!locationName || seen.has(locationName)) {
                                                            return false;
                                                        }
                                                        seen.add(locationName);
                                                        return locationName.includes(normalizedInput);
                                                    });
                                                }}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Location *"
                                                        placeholder="Type to search..."
                                                        error={!!formErrors.location?.location && locationType === 'location'}
                                                        sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                                    />
                                                )}
                                                slotProps={{
                                                    popper: {
                                                        sx: { zIndex: 1400 }
                                                    }
                                                }}
                                                fullWidth
                                                noOptionsText="No locations found"
                                            />
                                        )}

                                        {locationType === 'manual' && (
                                            <Controller
                                                name="location.location"
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        size="small"
                                                        fullWidth
                                                        label="Enter Location"
                                                        disabled={step1Submitted || isReadOnly}
                                                        sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Employees, Witnesses and Incident Type */}
                            <Grid container spacing={1}>
                                {/* Employees */}
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.25,
                                            mb: 0.5,
                                            minHeight: 24,
                                            maxHeight: 40,
                                            overflowY: 'auto'
                                        }}>
                                            {watchedValues.employees.map((emp, index) => (
                                                <Chip
                                                    key={index}
                                                    label={emp.FullName || emp.fullName}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                    onDelete={!step1Submitted ? () => {
                                                        setValue('employees',
                                                            watchedValues.employees.filter((_, i) => i !== index)
                                                        );
                                                    } : undefined}
                                                />
                                            ))}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            sx={{ height: 40 }}
                                            onClick={() => setEmployeeModalOpen(true)}
                                            disabled={step1Submitted}
                                        >
                                            Select Employees
                                        </Button>
                                    </Box>
                                </Grid>
                                
                                {/* Witnesses */}
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.25,
                                            mb: 0.5,
                                            minHeight: 24,
                                            maxHeight: 40,
                                            overflowY: 'auto'
                                        }}>
                                            {watchedValues.witnesses.map((witness, index) => (
                                                <Chip
                                                    key={index}
                                                    label={witness.FullName || witness.fullName}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                    onDelete={!step1Submitted ? () => {
                                                        setValue('witnesses',
                                                            watchedValues.witnesses.filter((_, i) => i !== index)
                                                        );
                                                    } : undefined}
                                                />
                                            ))}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            sx={{ height: 40 }}
                                            onClick={() => setWitnessModalOpen(true)}
                                            disabled={step1Submitted}
                                        >
                                            Select Witnesses
                                        </Button>
                                    </Box>
                                </Grid>
                                
                                {/* Incident Type(s) */}
                                <Grid item xs={12} sm={4}>
                                    <Controller
                                        name="incidentTypes"
                                        control={control}
                                        render={({ field }) => {
                                            const current = Array.isArray(field.value) ? field.value : [];
                                            return (
                                                <Autocomplete
                                                    multiple
                                                    options={INCIDENT_TYPES}
                                                    value={current}
                                                    onChange={(_, newValue) => {
                                                        const next = newValue || [];
                                                        field.onChange(next);
                                                        // keep legacy single for compatibility
                                                        const legacy = next.length > 0 ? next[0] : '';
                                                        setValue('incidentType', legacy);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            label="Incident Type(s)"
                                                            disabled={step1Submitted || isReadOnly}
                                                            sx={{ '& .MuiInputBase-root': { minHeight: 40 }, marginTop: '27px' }}
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Description */}
                            <Controller
                                name="description"
                                control={control}
                                rules={{ required: true, minLength: 10 }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        label="Brief Description"
                                        disabled={step1Submitted || isReadOnly}
                                        InputProps={{
                                            endAdornment: speechSupported && !step1Submitted && (
                                                <IconButton
                                                    onClick={toggleSpeechRecognition}
                                                    size="small"
                                                    color={isListening ? 'error' : 'default'}
                                                >
                                                    {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                </IconButton>
                                            )
                                        }}
                                    />
                                )}
                            />

                            {/* Submit button */}
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<SendIcon />}
                                onClick={handleStep1Submit}
                                disabled={!watchedValues.incidentDateTime || !watchedValues.description || 
                                         watchedValues.employees.length === 0 || !watchedValues.location?.location}
                                sx={{ alignSelf: 'center', px: 3, mt: 1 }}
                            >
                                Submit Initial Report
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
                ) : (
                    /* Show accordion after submission */
                    <Accordion 
                        expanded={initialInfoExpanded}
                        onChange={(e, expanded) => setInitialInfoExpanded(expanded)}
                        sx={{ 
                            backgroundColor: '#e8f5e9',
                            '& .MuiAccordionSummary-root': { minHeight: 40 }
                        }}
                    >
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ py: 0 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <CheckIcon color="success" sx={{ fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Initial Information (Completed)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {watchedValues.incidentDateTime ? dayjs(watchedValues.incidentDateTime).format('MM/DD/YYYY HH:mm') : ''} 
                                    {' • '}
                                    {watchedValues.employees?.length || 0} employees
                                    {' • '}
                                    {watchedValues.witnesses?.length || 0} witnesses
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                {/* First Row: Date/Time and Location - same as initial form */}
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            value={watchedValues.incidentDateTime ? dayjs(watchedValues.incidentDateTime).format('MM/DD/YYYY HH:mm') : ''}
                                            label="Date/Time"
                                            size="small"
                                            fullWidth
                                            disabled
                                            sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            value={watchedValues.location?.location || ''}
                                            label="Location"
                                            size="small"
                                            fullWidth
                                            disabled
                                            sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Second Row: Employees, Witnesses and Incident Type - same as initial form */}
                                <Grid container spacing={1}>
                                    {/* Employees */}
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Employees
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.25,
                                                minHeight: 40,
                                                p: 1,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 1,
                                                backgroundColor: '#f5f5f5'
                                            }}>
                                                {watchedValues.employees && watchedValues.employees.length > 0 ? (
                                                    watchedValues.employees.map((emp, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={emp.FullName || emp.fullName}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">None</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    
                                    {/* Witnesses */}
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Witnesses
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.25,
                                                minHeight: 40,
                                                p: 1,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 1,
                                                backgroundColor: '#f5f5f5'
                                            }}>
                                                {watchedValues.witnesses && watchedValues.witnesses.length > 0 ? (
                                                    watchedValues.witnesses.map((witness, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={witness.FullName || witness.fullName}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">None</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    
                                    {/* Incident Type */}
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            value={watchedValues.incidentType || ''}
                                            label="Incident Type"
                                            size="small"
                                            fullWidth
                                            disabled
                                            sx={{ '& .MuiInputBase-root': { height: 40 }, marginTop: '20px' }}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Description - same as initial form */}
                                <TextField
                                    value={watchedValues.description || ''}
                                    label="Brief Description"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    disabled
                                />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Отправлено, но дозаполняет не этот человек — показываем только статус. */}
                {step1Submitted && !canFillFollowUps && (
                    <Box sx={{ p: 2 }}>
                        <Paper sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <CheckIcon color="success" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Initial report submitted
                                    </Typography>
                                    {incident?.siNumber && (
                                        <Chip size="small" label={incident.siNumber} color="primary" variant="outlined" />
                                    )}
                                </Box>

                                {incident?.workflowStage === INCIDENT_STAGE.ASSIGNED ? (
                                    <Alert severity="info">
                                        Assigned to <strong>{incident.assignedToName}</strong>
                                        {incident.assignedAt ? ` on ${dayjs(incident.assignedAt).format('MM/DD/YYYY')}` : ''}.
                                        {' '}They will complete the full report.
                                    </Alert>
                                ) : incident?.workflowStage === INCIDENT_STAGE.COMPLETED ? (
                                    <Alert severity="success">
                                        The full report has been submitted
                                        {incident.completedByName ? ` by ${incident.completedByName}` : ''}.
                                        This incident will close once the safety team signs it off.
                                    </Alert>
                                ) : (
                                    <Alert severity="info">
                                        Your report has been sent to the safety team. Nothing else is required from you —
                                        the person assigned to this incident will complete the rest.
                                    </Alert>
                                )}

                                <Typography variant="caption" color="text.secondary">
                                    This record disappears from your list once the incident is closed.
                                </Typography>
                            </Stack>
                        </Paper>
                    </Box>
                )}

                {/* Пакет дозаполнения — только назначенному */}
                {step1Submitted && canFillFollowUps && (
                    <>
                        <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>
                            This incident is assigned to you{incident?.siNumber ? ` (${incident.siNumber})` : ''} —
                            {' '}please complete the forms below.
                        </Alert>
                        <FormNavigator
                            forms={forms}
                            activeFormId={activeFormId}
                            onFormSelect={setActiveFormId}
                            completionStatus={completionStatus}
                            height={navigatorHeight}
                            expandedColumns={expandedColumns}
                            onExpandToggle={(type, expanded) => {
                                setExpandedColumns(prev => ({
                                    ...prev,
                                    [type]: expanded
                                }));
                            }}
                        />
                        
                        {/* Active Form Content */}
                        <Box sx={{ 
                            flex: 1,
                            overflow: 'auto',
                            backgroundColor: '#fafafa',
                            p: 1,
                            height: `calc(100vh - 64px - 48px - 52px - ${initialInfoExpanded ? '280px' : '40px'} - ${navigatorHeight}px)`
                        }}>
                            {renderActiveForm()}
                        </Box>
                    </>
                )}

                {/* Modals */}
                <SelectEmployeesAndTempsModal
                    open={employeeModalOpen}
                    onClose={() => setEmployeeModalOpen(false)}
                    onSelect={(selected) => {
                        setValue('employees', selected);
                        setEmployeeModalOpen(false);
                    }}
                    alreadySelected={watchedValues.employees}
                />
                
                <SelectEmployeesAndTempsModal
                    open={witnessModalOpen}
                    onClose={() => setWitnessModalOpen(false)}
                    onSelect={(selected) => {
                        setValue('witnesses', selected);
                        setWitnessModalOpen(false);
                    }}
                    alreadySelected={watchedValues.witnesses}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default IncidentFormStep1New;
